/**
 * Счета группами по типу — те же четыре, что при создании счёта: карты,
 * наличные, накопительные, кредитки.
 *
 * Группы и порядок одни на всё приложение: список «Счета», выбор счёта в
 * записи, в выписке, в фильтрах «Операций» и «Аналитики». Человек расставил
 * счета один раз — и находит нужный на том же месте везде.
 *
 * Видов в данных больше четырёх: вклад, брокерский, счёт ребёнка остались от
 * первых версий. Создать их больше нельзя, и отдельной группы «Другие» у них
 * нет — это те же накопления.
 */
import { add, ZERO, type Account, type Money } from '@skoka/core';

export type AccountGroupKey = 'cards' | 'cash' | 'savings' | 'credit';

export const GROUP_TITLE: Record<AccountGroupKey, string> = {
  cards: 'Карты', cash: 'Наличные', savings: 'Накопительные', credit: 'Кредитки',
};

const DEFAULT_ORDER: readonly AccountGroupKey[] = ['cards', 'cash', 'savings', 'credit'];

/*
 * Порядок групп из последнего прочитанного состояния.
 *
 * Счета выбирают в двух десятках форм, и протаскивать порядок групп через
 * каждую значило бы трогать их все ради одного списка. Состояние читается
 * одним `loadState` — он и запоминает порядок здесь.
 */
let remembered: readonly string[] = [];
export const rememberGroupOrder = (groups: readonly string[]): void => { remembered = [...groups]; };

export const groupOf = (kind: Account['kind']): AccountGroupKey => (
  kind === 'debit' ? 'cards' : kind === 'cash' ? 'cash' : kind === 'credit_card' ? 'credit' : 'savings');

/** Порядок групп: сохранённый человеком, недостающие — в обычном порядке в конце. */
export function groupOrder(saved: readonly string[] = remembered): AccountGroupKey[] {
  const known = saved.filter((k): k is AccountGroupKey => (DEFAULT_ORDER as readonly string[]).includes(k));
  const unique = [...new Set(known)];
  return [...unique, ...DEFAULT_ORDER.filter((k) => !unique.includes(k))];
}

export interface AccountGroup {
  key: AccountGroupKey;
  title: string;
  accounts: Account[];
  /** Сумма остатков группы; у кредиток — минусом, это долг. */
  total: Money;
}

/**
 * Счета по порядку человека: группа, место в группе.
 *
 * Без места — после расставленных, по названию. Хранилище отдаёт счета в
 * порядке случайных идентификаторов, и «как появились» из него не узнать, а
 * по названию список хотя бы не прыгает от устройства к устройству.
 */
export function sortAccounts<T extends Pick<Account, 'kind' | 'order' | 'name'>>(accounts: readonly T[], saved: readonly string[] = remembered): T[] {
  const groups = groupOrder(saved);
  return [...accounts].sort((x, y) => groups.indexOf(groupOf(x.kind)) - groups.indexOf(groupOf(y.kind))
    || (x.order ?? Number.MAX_SAFE_INTEGER) - (y.order ?? Number.MAX_SAFE_INTEGER)
    || x.name.localeCompare(y.name, 'ru'));
}

/** Непустые группы в порядке человека. */
export function accountGroups(accounts: readonly Account[], saved: readonly string[] = remembered): AccountGroup[] {
  const sorted = sortAccounts(accounts, saved);
  return groupOrder(saved)
    .map((key) => {
      const list = sorted.filter((a) => groupOf(a.kind) === key);
      return { key, title: GROUP_TITLE[key], accounts: list, total: list.length ? add(...list.map((a) => a.balance)) : ZERO };
    })
    .filter((g) => g.accounts.length > 0);
}

/**
 * Счета, с которых платят по долгам и платежам: все, кроме кредиток и архива.
 *
 * «Не для трат» значит «не считать в «Можно тратить»», а не «с него нельзя
 * платить»: досрочно по кредиту чаще всего вносят как раз с накопительного.
 * Раньше выбор показывал только счета для трат, и накопительного в нём не
 * было. Счета для трат — первыми: с первого платят по умолчанию.
 */
export function payAccounts(accounts: readonly Account[]): Account[] {
  const all = accounts.filter((a) => !a.archived && a.kind !== 'credit_card');
  return [...all.filter((a) => a.isLiquid), ...all.filter((a) => !a.isLiquid)];
}
